'use client';

import { X } from 'lucide-react';
import { Button } from '@/atoms/Button/Button';
import { Card, CardContent } from '@/atoms/Card/Card';
import { Container } from '@/atoms/Container/Container';
import { Typography } from '@/atoms/Typography/Typography';
import { PostLinkEmbeds } from '@/molecules/PostLinkEmbeds/PostLinkEmbeds';
import { PostPreviewNavigableProvider } from '@/molecules/PostPreviewCard/PostPreviewNestingContext';
import type { PostInputLinkEmbedProps } from './PostInputLinkEmbed.types';

/**
 * The link a new post embeds, shown in the composer the way a repost shows the post it quotes: the
 * link's preview (its external resource card when Pinakes knows it) in a card, with a button that
 * removes the embed. The link's preview must not navigate away from the post being written.
 */
export function PostInputLinkEmbed({ url, onRemove, disabled = false }: PostInputLinkEmbedProps) {
  return (
    <Card
      data-testid="post-input-link-embed"
      role="group"
      aria-label="Embedded link"
      className="w-full max-w-full min-w-0 gap-0 rounded-md bg-card py-0"
    >
      <CardContent className="flex w-full max-w-full min-w-0 flex-col gap-3 p-3">
        <Container overrideDefaults className="flex min-w-0 items-center justify-between gap-2">
          {/* The link names the embed even when its preview renders nothing */}
          <Typography size="sm" className="min-w-0 truncate text-muted-foreground">
            Embedded link · {url.replace(/^https?:\/\//, '')}
          </Typography>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            disabled={disabled}
            aria-label="Remove embedded link"
            data-cy="post-input-link-embed-remove"
            className="size-8 shrink-0"
          >
            <X className="size-4" />
          </Button>
        </Container>
        <PostPreviewNavigableProvider navigable={false}>
          <PostLinkEmbeds url={url} />
        </PostPreviewNavigableProvider>
      </CardContent>
    </Card>
  );
}
